"use client";
import { cn } from "@/lib/utils";
import ChoicePills from "../choicePills/choicePills.component";
import {
  accentClasses,
  type IndustryAccent,
} from "../industryTheme/industryTheme.util";
import { useBeforeAfter } from "./hooks";

interface BeforeAfterMoment {
  title: string;
  text: string;
}

// How a step looks in the "without" story: "same" = nothing different
// (usually the first visit), "faded" = a missed moment (grey, dashed),
// "lost" = where the customer is lost (red). Defaults by position: first
// "same", last "lost", the rest "faded".
export type BeforeAfterOutcome = "same" | "faded" | "lost";

export interface BeforeAfterStep {
  // Timeline marker above the dot, e.g. "Week 1", "Visit 5", "Quiet hours".
  marker: string;
  with: BeforeAfterMoment;
  without: BeforeAfterMoment;
  outcome?: BeforeAfterOutcome;
}

interface BeforeAfterTimelineProps {
  // Accessible name of the whole figure.
  ariaLabel: string;
  // Accessible name of the two-button switch (e.g. "Compare").
  toggleLabel: string;
  withLabel: string;
  withoutLabel: string;
  steps: BeforeAfterStep[];
  summaryWith: string;
  summaryWithout: string;
  caption?: string;
  accent?: IndustryAccent;
}

const outcomeAt = (step: BeforeAfterStep, index: number, count: number) =>
  step.outcome ?? (index === 0 ? "same" : index === count - 1 ? "lost" : "faded");

// "A year with and without a loyalty programme" - the preview's
// `before_after` visual. A two-button switch flips every step of a
// customer's timeline between the Fydo story and the no-loyalty story; the
// accent line along the top drains away in the "without" story.
const BeforeAfterTimeline = ({
  ariaLabel,
  toggleLabel,
  withLabel,
  withoutLabel,
  steps,
  summaryWith,
  summaryWithout,
  caption,
  accent = "brand",
}: BeforeAfterTimelineProps) => {
  const { mode, isWithout, setMode } = useBeforeAfter();
  const tone = accentClasses[accent];
  const toggles = [
    { key: "without" as const, label: withoutLabel },
    { key: "with" as const, label: withLabel },
  ];

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div className="mx-auto grid max-w-[1040px] gap-6">
        <ChoicePills
          ariaLabel={toggleLabel}
          options={toggles}
          value={mode}
          onChange={setMode}
          accent={accent}
          isCentered
        />

        <div className="relative grid gap-4 pt-1.5 lg:auto-cols-fr lg:grid-flow-col lg:gap-3">
          {/* Line behind the dots (wide screens only), centred on them: the
              row's 6px top padding + the 18px marker + the 10px gap + half
              the 26px dot = 47px, minus half the 4px line = 45px. Its
              accent fill drains away when the "without" story is shown. */}
          <div
            aria-hidden="true"
            className="absolute top-[45px] right-[5%] left-[5%] hidden h-1 rounded bg-line lg:block"
          >
            <div
              className={cn(
                "h-full rounded transition-[width] duration-700 ease-out motion-reduce:transition-none",
                tone.solid,
                isWithout ? "w-0" : "w-full",
              )}
            />
          </div>

          {steps.map((step, index) => {
            const outcome = isWithout
              ? outcomeAt(step, index, steps.length)
              : "same";
            const moment = isWithout ? step.without : step.with;
            return (
              <div
                key={step.marker}
                className="relative grid grid-cols-[auto_1fr] content-start items-start gap-x-3.5 gap-y-1.5 lg:grid-cols-1 lg:grid-rows-[auto_auto_1fr] lg:justify-items-center lg:gap-2.5 lg:text-center"
              >
                <span
                  className={cn(
                    "col-span-full h-[18px] text-[12px] leading-[18px] font-extrabold tracking-[0.08em] uppercase",
                    tone.text,
                  )}
                >
                  {step.marker}
                </span>
                <i
                  aria-hidden="true"
                  className={cn(
                    "relative z-[1] size-[26px] rounded-full border-[5px] border-white ring-[3px] transition-colors duration-500 motion-reduce:transition-none",
                    outcome === "faded"
                      ? "bg-line ring-paper"
                      : outcome === "lost"
                        ? "bg-primary ring-tone-blush"
                        : cn(tone.solid, tone.ring),
                  )}
                />
                <div
                  className={cn(
                    "grid w-full content-start gap-1 rounded-[20px] p-3.5 lg:h-full",
                    outcome === "faded"
                      ? "border-2 border-dashed border-line bg-cream"
                      : outcome === "lost"
                        ? "border-2 border-line-warm bg-tone-blush"
                        : "bg-white shadow-card",
                  )}
                >
                  <b className="text-[14.5px] leading-snug font-bold text-ink">
                    {moment.title}
                  </b>
                  <small className="text-[13px] leading-snug text-ink-muted">
                    {moment.text}
                  </small>
                </div>
              </div>
            );
          })}
        </div>

        <p
          aria-live="polite"
          className="m-0 text-center text-[1.05rem] leading-snug font-bold text-ink"
        >
          {isWithout ? summaryWithout : summaryWith}
        </p>
      </div>
      {caption && (
        <figcaption className="px-1 pt-3 text-[12.5px] text-ink-muted">
          {caption}
        </figcaption>
      )}
    </figure>
  );
};

export default BeforeAfterTimeline;
